import { IPromise, IQService } from 'angular';
import { call, put } from 'redux-saga/effects';
import * as _ from 'underscore';
import { Messages } from '../../../../codegen/Localize';
import { RequestHelper } from '../../../../ts/helpers/RequestHelper';
import { ResultHelper } from '../../../../ts/helpers/ResultHelper';
import { DatasourceDefinition, WorkbookDefinition } from '../../../../ts/models/ContentDefinitions';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { IDataSourceInfo } from '../../../../ts/models/IDataSourceInfo';
import { IExtractTaskInfo } from '../../../../ts/models/IExtractTaskInfo';
import { IWorkbookInfo } from '../../../../ts/models/IWorkbookInfo';
import { RemoteRefreshTypes } from '../../../../ts/ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { RunExtractRefreshesOnDatasourcesRequest } from '../../../../ts/ServerApi/requests/RunExtractRefreshesOnDatasourcesRequest';
import { RunExtractRefreshesOnWorkbooksRequest } from '../../../../ts/ServerApi/requests/RunExtractRefreshesOnWorkbooksRequest';
import { IResult } from '../../../../ts/ServerApi/types/IResponse';
import { ServerService } from '../../../../ts/services/ServerService';
import { ToasterService, ToastType } from '../../../../ts/services/ToasterService';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { requestSpaceContents } from '../../../actions/spaces/RequestSpaceContentsActions';
import { RunExtractRefreshNowAction } from '../../../actions/spaces/RunExtractRefreshNowAction';
import { ServiceLocator } from '../../../ServiceLocator';
import { getDatasourceInfos, getWorkbookInfos } from '../SpacesSagaHelpers';


export function* handleRunExtractRefreshNowAction(action: RunExtractRefreshNowAction) {
    try {
        // Needed because the content info may have an incorrect ID for unknown reasons
        const contentDictionaryByType = _.groupBy(action.tasks, c => c.contentType);
        const workbookInfos = contentDictionaryByType[WorkbookDefinition.contentType] && (yield call(getWorkbookInfos, contentDictionaryByType[WorkbookDefinition.contentType]));
        const datasourceInfos = contentDictionaryByType[DatasourceDefinition.contentType] && (yield call(getDatasourceInfos, contentDictionaryByType[DatasourceDefinition.contentType]));
        yield call(executeRunExtractRefresh, datasourceInfos || [], workbookInfos || []);
        yield put(requestSpaceContents());
    } catch (e) {
        yield put(requestFailed(e, Messages.unexpectedError()));
    }
}

export function executeDatasourceRunNow(ids: string[], extractType: string): IPromise<IResult> {
    const ServerService = ServiceLocator.get<ServerService>('ServerService');
    return ServerService.sendRequests(
        RequestHelper.buildRequests(ids, (idsBlock) =>
            new RunExtractRefreshesOnDatasourcesRequest(idsBlock, extractType)
        ),
        ResultHelper.combineResults
    );
}

export function executeWorkbookRunNow(ids: string[], extractType: string): IPromise<IResult> {
    const ServerService = ServiceLocator.get<ServerService>('ServerService');
    return ServerService.sendRequests(
        RequestHelper.buildRequests(ids, (idsBlock) =>
            new RunExtractRefreshesOnWorkbooksRequest(idsBlock, extractType)
        ),
        ResultHelper.combineResults);
}

export function executeRunExtractRefresh(datasources: IDataSourceInfo[], workbooks: IWorkbookInfo[]): IPromise<void> {
    const supportsIncremental = (item: IContentInfo) => RemoteRefreshTypes.Incremental in item;
    const doesNotSupportIncremental = (item: IContentInfo) => !(RemoteRefreshTypes.Incremental in item);
    const PromiseService = ServiceLocator.get<IQService>('$q');
    // we do some casts so we have a try/catch to avoid any nasty surprises
    try {
        let requestPromises = [];
        const incrementalDatasourceIds = datasources.filter(supportsIncremental).map(item => item.id);
        if (incrementalDatasourceIds.length > 0) {
            requestPromises.push(executeDatasourceRunNow(incrementalDatasourceIds, 'IncrementExtract'));
        }
        const nonIncrementalDatasourceIds = datasources.filter(doesNotSupportIncremental).map(item => item.id);
        if (nonIncrementalDatasourceIds.length > 0) {
            requestPromises.push(executeDatasourceRunNow(nonIncrementalDatasourceIds, 'RefreshExtract'));
        }

        const incrementalWorkbookIds = workbooks.filter(supportsIncremental).map(item => item.id);
        if (incrementalWorkbookIds.length > 0) {
            requestPromises.push(executeWorkbookRunNow(incrementalWorkbookIds, 'IncrementExtract'));
        }
        const nonIncrementalWorkbookIds = workbooks.filter(doesNotSupportIncremental).map(item => item.id);
        if (nonIncrementalWorkbookIds.length > 0) {
            requestPromises.push(executeWorkbookRunNow(nonIncrementalWorkbookIds, 'RefreshExtract'));
        }
        const combinedPromise = PromiseService.all(requestPromises);
        return combinedPromise.then(results => {
            const succesfullRequests = results.filter(result => !result.errors || result.errors.length === 0);
            if (succesfullRequests.length > 0) {
                ToasterService.getInstance().pop({
                    type: ToastType.Info,
                    text: Messages.runNowSucceeded({
                        NUM_TASKS: String(succesfullRequests.length),
                    }),
                });
            }
            const failedRequests = results.filter(result => result.errors && result.errors.length > 0);
            if (failedRequests.length > 0) {
                ToasterService.getInstance().pop({
                    type: ToastType.Error,
                    text: Messages.runNowFailed({
                        NUM_TASKS: String(failedRequests.length),
                    }),
                });
            }
        });
    } catch (exception) {
        ToasterService.getInstance().pop({
            type: ToastType.Error,
            text: Messages.runNowFailed({
                NUM_TASKS: String(datasources.length + workbooks.length),
            }),
        });
    }
    return;
}
